/**
 * Professional SVG icon set — no emojis anywhere.
 */
import type { SVGProps } from "react";

type P = SVGProps<SVGSVGElement>;
const d: P = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function IconChat(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
export function IconWorkflow(p: P) {
  return (
    <svg {...d} {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="8" y="14" width="7" height="7" rx="1" />
      <path d="M6.5 10v1.5a2 2 0 0 0 2 2h1M17.5 10v1.5a2 2 0 0 1-2 2h-1" />
    </svg>
  );
}
export function IconSend(p: P) {
  // Clean arrow-up — simpler than the paper-plane, reads as "send"
  // when paired with a primary-coloured tile (see chat-composer-send).
  return (
    <svg {...d} {...p}>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}
export function IconStop(p: P) {
  // Filled square — reads as "halt the in-flight turn." Renders on
  // the composer's send-tile while sending=true (the same tile flips
  // ink-2 + cream icon so it's clearly a different action mode).
  return (
    <svg {...d} {...p}>
      <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
export function IconPlus(p: P) {
  return (
    <svg {...d} {...p}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
export function IconTrash(p: P) {
  return (
    <svg {...d} {...p}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
export function IconPlay(p: P) {
  return (
    <svg {...d} {...p}>
      <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />
    </svg>
  );
}
export function IconDownload(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
export function IconRefresh(p: P) {
  return (
    <svg {...d} {...p}>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}
export function IconClear(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}
export function IconTerminal(p: P) {
  return (
    <svg {...d} {...p}>
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </svg>
  );
}
export function IconCheck(p: P) {
  return (
    <svg {...d} {...p}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
export function IconUpload(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
export function IconX(p: P) {
  return (
    <svg {...d} {...p}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
export function IconHelp(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
export function IconChevron(p: P) {
  return (
    <svg {...d} {...p}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
export function IconPanel(p: P) {
  return (
    <svg {...d} {...p}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}
export function IconReport(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="8" y1="9" x2="10" y2="9" />
    </svg>
  );
}
export function IconClock(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/* ── Foreman brand marks ── */
export function ForemanLogo(p: P) {
  return (
    <svg viewBox="6 6 88 88" fill="none" {...p}>
      <g stroke="currentColor" strokeWidth="4">
        <circle cx="50" cy="50" r="28" />
        <line x1="50" y1="10" x2="50" y2="18" />
        <line x1="50" y1="82" x2="50" y2="90" />
        <line x1="10" y1="50" x2="18" y2="50" />
        <line x1="82" y1="50" x2="90" y2="50" />
        <circle cx="50" cy="50" r="4" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

export function ForemanSpinner({
  className,
  ...p
}: P & { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" fill="none" className={className} {...p}>
      <g stroke="currentColor" strokeWidth="4">
        <circle cx="50" cy="50" r="28" />
        <line x1="50" y1="10" x2="50" y2="18" />
        <line x1="50" y1="82" x2="50" y2="90" />
        <line x1="10" y1="50" x2="18" y2="50" />
        <line x1="82" y1="50" x2="90" y2="50" />
        <circle cx="50" cy="50" r="4" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

// --- Workflow graph node icons ---
export function IconNodeInput(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="7" y1="9" x2="17" y2="9" />
      <line x1="7" y1="13" x2="13" y2="13" />
    </svg>
  );
}
export function IconNodeOutput(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
export function IconNodeTool(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}
export function IconNodeFilter(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
export function IconNodeAI(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <path d="M12 2l1.09 3.26L16 6l-2.91.74L12 10l-1.09-3.26L8 6l2.91-.74L12 2z" />
      <path d="M19 9l.62 1.88L21.5 11.5l-1.88.62L19 14l-.62-1.88L16.5 11.5l1.88-.62L19 9z" />
      <path d="M6 14l.62 1.88L8.5 16.5l-1.88.62L6 19l-.62-1.88L3.5 16.5l1.88-.62L6 14z" />
    </svg>
  );
}
export function IconNodeTransform(p: P) {
  return (
    <svg {...d} width={20} height={20} {...p}>
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  );
}
export function IconWatchdog(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
export function IconTrigger(p: P) {
  return (
    <svg {...d} {...p}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
export function IconMemory(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M12 2a7 7 0 0 1 7 7c0 3-2 5.5-4 7.5S12 20 12 22c0-2-1-2.5-3-4.5S5 12 5 9a7 7 0 0 1 7-7z" />
      <path d="M9 9h6" />
      <path d="M9 13h6" />
    </svg>
  );
}
export function IconSkills(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M12 2l1.09 3.26L16 6l-2.91.74L12 10l-1.09-3.26L8 6l2.91-.74L12 2z" />
      <path d="M5 15l.62 1.88L7.5 17.5l-1.88.62L5 20l-.62-1.88L2.5 17.5l1.88-.62L5 15z" />
      <path d="M19 15l.62 1.88L21.5 17.5l-1.88.62L19 20l-.62-1.88L16.5 17.5l1.88-.62L19 15z" />
    </svg>
  );
}
export function IconOrg(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
export function IconEmail(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
export function IconGateway(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}
export function IconWhatsApp(p: P) {
  return (
    <svg {...d} {...p} viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
    </svg>
  );
}
export function IconSearch(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
export function IconDollar(p: P) {
  return (
    <svg {...d} {...p}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
export function IconLightbulb(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A7 7 0 1 0 4.5 11.5c.76.76 1.23 1.52 1.41 2.5" />
    </svg>
  );
}
export function IconBarChart(p: P) {
  return (
    <svg {...d} {...p}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}
export function IconClipboard(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}

/* ── Sidebar nav + app-shell icons.
      Geometry must match the kit specimen in
      Foreman Hub/design/ui/kit.html (Section 11). ── */
export function IconSidebarCollapse(p: P) {
  return (
    <svg {...d} {...p}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
    </svg>
  );
}
export function IconActions(p: P) {
  return (
    <svg {...d} {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
export function IconReports(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-6" />
    </svg>
  );
}
export function IconLookouts(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
export function IconAudit(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </svg>
  );
}
export function IconInputForm(p: P) {
  return (
    <svg {...d} {...p}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <line x1="7" y1="11" x2="13" y2="11" />
      <line x1="7" y1="14.5" x2="11" y2="14.5" />
      <line x1="17" y1="11" x2="17" y2="14.5" />
    </svg>
  );
}
export function IconFileDrop(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <polyline points="14 3 14 8 19 8" />
      <polyline points="9 14 12 11 15 14" />
      <line x1="12" y1="11" x2="12" y2="17" />
    </svg>
  );
}
export function IconKnowledge(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-4 10.5c.5.5.8 1.2.8 1.9V18h6.4v-2.6c0-.7.3-1.4.8-1.9A6 6 0 0 0 12 3z" />
    </svg>
  );
}
export function IconEdit(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
export function IconSettings(p: P) {
  return (
    <svg {...d} {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}
export function IconSignOut(p: P) {
  return (
    <svg {...d} {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
